import React from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { KpiCard } from '../../../../components/financeiro';
import { formatCurrency } from '../../../../utils/money';
import type {
  ResumoFinanceiro,
  PontoSerieFinanceira,
  QuebraFinanceira,
  CustoCampanhaCupom,
  PeriodoFinanceiro,
} from '../../../../types/financeiro';

// Recebe tudo por props vindas do hub — a aba não busca nada (pages → components → hooks →
// services → apollo). Espelha a ordem dos 4 estados obrigatórios de
// pages/liga/financeiro/ResumoLigaTab.tsx:46-74 (erro de rede → indisponível → loading → vazio
// específico), escopados para "resumo-plataforma-*" (a suíte desta fase distingue os 3 estados
// positiva e negativamente por eles — AC-A6).
//
// 5 blocos do docs/reports/financeiro/08-telas-admin.md §2: (1) Receita InPlay — taxa + boletos
// + soma rotulada, a soma vem SEMPRE de resumo.totalReceitaInPlay, NUNCA recalculada aqui
// (AC-A3); (2) Volume transacionado — GMV rotulado "Volume transacionado", nunca com o rótulo
// proibido pela RN-04 (ver o grep de Level 2 desta fase) nem "receita", com a nota fixa de
// quadras; (3) Evolução — gráfico de série, datetimeUTC: false; (4) Meio de pagamento — usa
// chaveNome/valor, nunca categoriaNome; (5) Campanhas — tabela de cupons.

export interface ResumoTabProps {
  resumo: ResumoFinanceiro | null;
  serie: PontoSerieFinanceira[];
  quebraPorMeioPagamento: QuebraFinanceira[];
  campanhas: CustoCampanhaCupom[];
  loading: boolean;
  error: Error | undefined;
  resumoIndisponivel: boolean;
  onRetry: () => void;
  periodo: PeriodoFinanceiro;
}

function formatDataBr(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY');
}

const ResumoTab: React.FC<ResumoTabProps> = ({
  resumo,
  serie,
  quebraPorMeioPagamento,
  campanhas,
  loading,
  error,
  resumoIndisponivel,
  onRetry,
  periodo,
}) => {
  // Falha de rede em qualquer uma das 4 queries do hook — mensagem distinta do estado
  // "período sem movimento" (AC-A6).
  if (error) {
    return (
      <div className="text-center py-5" data-testid="resumo-plataforma-erro">
        <p className="text-danger mb-3">
          Não foi possível carregar o resumo financeiro da plataforma. Tente novamente.
        </p>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    );
  }

  // resumoFinanceiroPlataforma retornando null SEM erro (contrato nullable) — estado tratado,
  // distinto do erro de rede.
  if (resumoIndisponivel) {
    return (
      <div className="text-center py-5" data-testid="resumo-plataforma-indisponivel">
        <p className="text-muted mb-3">
          O resumo financeiro da plataforma não está disponível no momento.
        </p>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (loading || !resumo) {
    return <div className="text-center py-5">Carregando resumo financeiro...</div>;
  }

  const chartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    dataLabels: { enabled: false },
    xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
    yaxis: { labels: { formatter: (valor: number) => formatCurrency(valor) } },
    tooltip: { x: { format: 'dd/MM/yyyy' } },
    plotOptions: { bar: { columnWidth: '55%' } },
  };
  const chartSeries = [
    {
      name: 'Volume transacionado por dia',
      data: serie.map((ponto) => ({ x: dayjs(ponto.data).valueOf(), y: ponto.valor })),
    },
  ];

  return (
    <div>
      <div className="mb-3">
        <h5 className="fw-700 mb-1">Resumo financeiro da plataforma</h5>
        <span className="text-grey-500 font-xsss">
          Totais calculados no servidor para o período selecionado — nenhum valor é somado no
          cliente.
        </span>
      </div>

      {/* Bloco 1 — Receita InPlay (D-12): taxa + boletos + soma rotulada. A soma vem sempre do
          campo do servidor, nunca recalculada no cliente (AC-A3). */}
      <div className="row g-3 mb-4">
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Taxa retida"
            valor={resumo.totalTaxaPlataforma}
            legenda="Retida pela InPlay no período"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Boletos de ligas"
            valor={resumo.totalReceitaBoletosLiga ?? 0}
            legenda="Boletos pagos por ligas no período"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Receita InPlay (taxa + boletos)"
            valor={resumo.totalReceitaInPlay ?? 0}
            legenda="Soma das duas fontes de receita própria"
          />
        </div>
      </div>

      {/* Bloco 2 — Volume transacionado. Rótulo obrigatório "Volume transacionado" (RN-04,
          AC-A2) — nunca o termo proibido pelo grep de Level 2 nem "receita". */}
      <div className="row g-3 mb-2">
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Volume transacionado"
            valor={resumo.totalRecebido}
            legenda="Inscrições pagas no período"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Repasse às ligas"
            valor={resumo.totalRepasseLiga}
            legenda="Valor líquido repassado às ligas"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard titulo="Em aberto" valor={resumo.totalEmAberto} legenda="Inscrições ainda a receber" />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Estornado"
            valor={resumo.totalEstornado}
            legenda="Valores devolvidos a atletas"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Inscrições gratuitas"
            valor={resumo.quantidadeGratuita}
            legenda="Volume sem receita"
          />
        </div>
      </div>
      <p className="text-grey-500 font-xssss mb-4">
        Não inclui aluguel de quadras (fora desta entrega).
      </p>

      {/* Bloco 3 — Evolução */}
      {serie.length === 0 ? (
        <div className="text-center py-5" data-testid="resumo-plataforma-serie-vazia">
          <p className="mb-3">
            Nenhum lançamento entre {formatDataBr(periodo.inicio)} e {formatDataBr(periodo.fim)}.
          </p>
          <p className="text-grey-500">Tente ampliar o período para ver mais lançamentos.</p>
        </div>
      ) : (
        <div className="card border-0 shadow-xss rounded-xxl p-3 mb-4">
          <Chart options={chartOptions} series={chartSeries} type="bar" width="100%" />
        </div>
      )}

      {/* Bloco 4 — Meio de pagamento. Usa chaveNome/valor, nunca categoriaNome (null nesta
          dimensão). */}
      <div className="card border-0 shadow-xss rounded-xxl p-3 mb-4">
        <h6 className="fw-700 mb-3">Por meio de pagamento</h6>
        {quebraPorMeioPagamento.length === 0 ? (
          <p className="text-grey-500 mb-0">Nenhum lançamento por meio de pagamento no período.</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {quebraPorMeioPagamento.map((linha) => (
              <li
                key={linha.chaveId ?? linha.chaveNome ?? 'sem-meio'}
                className="d-flex justify-content-between mb-2"
              >
                <span>{linha.chaveNome ?? 'Não informado'}</span>
                <span className="fw-600">{formatCurrency(linha.valor)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bloco 5 — Campanhas */}
      <div className="card border-0 shadow-xss rounded-xxl p-3">
        <h6 className="fw-700 mb-3">Campanhas</h6>
        {campanhas.length === 0 ? (
          <p className="text-grey-500 mb-0">Nenhuma campanha de cupom usada no período.</p>
        ) : (
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Código</th>
                <th>Usos</th>
                <th>Desconto concedido</th>
              </tr>
            </thead>
            <tbody>
              {campanhas.map((campanha) => (
                <tr key={campanha.codigo}>
                  <td>{campanha.codigo}</td>
                  <td>{campanha.usos}</td>
                  <td>{formatCurrency(campanha.totalDesconto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ResumoTab;
